<template>
  <div class="course-builder">
    <Sidebar />
    <div class="course-builder-content">
      <div class="course-builder-header">
        <h2>Create New Course</h2>
        <div class="progress-bar">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="progress-step"
            :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
          >
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-label">{{ step.label }}</div>
            <div class="step-connector" v-if="index < steps.length - 1"></div>
          </div>
        </div>
      </div>

      <div class="step-container">
        <div class="step-sidebar">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="step-tab"
            :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
            @click="goToStep(index)"
          >
            <div class="step-tab-number">{{ index + 1 }}</div>
            <div class="step-tab-label">{{ step.label }}</div>
            <div class="step-tab-status">
              <span v-if="currentStep > index" class="status-icon">✓</span>
              <span v-else-if="currentStep === index" class="status-icon">●</span>
            </div>
          </div>
        </div>

        <div class="step-content">
          <!-- Basic Information Step -->
          <div v-if="currentStep === 0" class="step">
            <h3>Basic Information</h3>
            <div class="form-group">
              <label for="title_en">Course Title *</label>
              <input
                id="title_en"
                v-model="formData.title_en"
                type="text"
                class="form-control"
                required
              />
            </div>
            <div class="form-group">
              <label for="title_ar">Course Title(Arabic) *</label>
              <input
                id="title_ar"
                v-model="formData.title_ar"
                type="text"
                class="form-control"
                dir="rtl"
                lang="ar"
                required
              />
            </div>
            <div class="form-group">
              <label for="science_behind_it">Science Behind It</label>
              <input
                id="science_behind_it"
                type="file"
                class="form-control"
                accept="image/*"
                @change="(e) => handleFileUpload(e, 'science_behind_it')"
              >
            </div>
            <div class="form-group">
              <label for="cover_image">Cover Image (English) *</label>
              <input
                id="cover_image"
                type="file"
                class="form-control"
                accept="image/jpeg,image/png,image/jpg"
                @change="(e) => handleFileUpload(e, 'cover_image')"
                required
              >
            </div>
            <div class="form-group">
              <label for="cover_image_ar">Cover Image (Arabic) *</label>
              <input
                id="cover_image_ar"
                type="file"
                class="form-control"
                accept="image/jpeg,image/png,image/jpg"
                @change="(e) => handleFileUpload(e, 'cover_image_ar')"
                required
              >
            </div>
            <div class="form-group">
              <label for="category">Category *</label>
              <select
                id="category"
                v-model="formData.category"
                class="form-control"
                required
              >
                <option value="">Select a category</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                    {{ cat.name }} {{ cat.title_ar ? `- ${cat.title_ar}` : '' }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="instructor">Instructor *</label>
              <select
                id="instructor"
                v-model="formData.instructor"
                class="form-control"
                required
              >
                <option value="">Select an instructor</option>
                <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.id">
                    {{ instructor?.first_name || '' }} {{ instructor?.last_name || '' }}
                </option>
              </select>
            </div>
            <label for="description">Course Description *</label>
            <ckeditor
              :editor="editor"
              v-model="formData.description"
              :config="editorConfig"
              tag-name="textarea"
              required
            />
            <label for="description">Course Description(Arabic) *</label>
            <ckeditor
              :editor="editor"
              v-model="formData.description_ar"
              :config="arabicEditorConfig"
              tag-name="textarea"
              required
            />
          </div>

          <!-- Move pricing type and price inside step 0 -->
          <div v-if="currentStep === 0">
            <div class="form-group">
              <label>Pricing Type *</label>
              <select v-model="formData.pricingType" class="form-control" required>
                <option value="">Select pricing type</option>
                <option value="subscription">Included in Subscription</option>
                <option value="paid">Paid Course</option>
              </select>
            </div>

            <div class="form-group" v-if="formData.pricingType === 'paid'">
              <label for="price">Course Price (USD) *</label>
              <input
                id="price"
                v-model="formData.price"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                required
              />
            </div>
          </div>

          <!-- Course Structure Step -->
          <div v-if="currentStep === 1" class="step">
            <h3>Course Structure</h3>
            <div class="form-group">
              <label for="duration">Estimated Duration (hours)</label>
              <input
                id="duration"
                v-model.number="formData.duration"
                type="number"
                class="form-control"
                min="1"
                step="0.5"
                required
              />
            </div>
            <div class="form-group">
              <label>Course Level</label>
              <select v-model="formData.level" class="form-control" required>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <!-- Course Content Step -->
          <div v-if="currentStep === 2" class="step">
            <h3>Course Content</h3>
            <div class="sections-container">
              <div v-for="(section, index) in formData.sections" :key="index" class="section">
                <div class="section-header">
                  <h4>Section {{ index + 1 }}</h4>
                  <button @click="removeSection(index)" class="btn-danger" v-if="formData.sections.length > 1">
                    Remove
                  </button>
                </div>
                <div class="form-group">
                  <input
                    v-model="section.title"
                    type="text"
                    class="form-control"
                    placeholder="Section Title"
                    required
                  />
                </div>
                <div class="form-group">
                  <ckeditor
                    :editor="editor"
                    v-model="section.description"
                    :config="editorConfig"
                    tag-name="textarea"
                    required
                  />
                </div>
                <div class="form-group">
                  <label for="video">Video * ({{ lessons.length }} available)</label>
                  <select 
                    :id="'video_' + index"
                    class="form-control"
                    v-model="section.video_id"
                    required
                  >
                    <option value="">Select a video</option>
                    <option 
                      v-for="lesson in lessons" 
                      :key="lesson.id"
                      :value="lesson.id"
                    >
                      {{ lesson.title }}
                    </option>
                  </select>
                  <!-- Debug output -->
                  <small v-if="!lessons.length" class="text-muted">Loading lessons...</small>
                </div>
              </div>
              <button @click="addSection" class="btn-primary">Add Section</button>
            </div>
          </div>

          <!-- Review Step -->
          <div v-if="currentStep === 3" class="step">
            <h3>Review Course Details</h3>
            <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
            <div class="review-content">
              <div class="review-section">
                <h4>Basic Information</h4>
                <p><strong>Title (English):</strong> {{ formData.title_en }}</p>
                <p><strong>Title (Arabic):</strong> {{ formData.title_ar }}</p>
                <div><strong>Description (English):</strong> <div v-html="formData.description"></div></div>
                <div><strong>Description (Arabic):</strong> <div v-html="formData.description_ar"></div></div>
                <p><strong>Category:</strong> {{ categories.find(c => c.id === formData.category)?.name }}</p>
                <p><strong>Instructor:</strong> {{ 
                  Array.isArray(instructors) && instructors.find(i => i.id === formData.instructor)?.first_name
                    ? `${instructors.find(i => i.id === formData.instructor)?.first_name} ${instructors.find(i => i.id === formData.instructor)?.last_name}`
                    : 'No instructor selected'
                }}</p>
                <p><strong>Pricing Type:</strong> {{ formData.pricingType === 'subscription' ? 'Included in Subscription' : 'Paid Course' }}</p>
                <p v-if="formData.pricingType === 'paid'"><strong>Price:</strong> ${{ formData.price }}</p>
              </div>
              <div class="review-section">
                <h4>Course Structure</h4>
                <p><strong>Duration:</strong> {{ formData.duration }} hours</p>
                <p><strong>Level:</strong> {{ formData.level.charAt(0).toUpperCase() + formData.level.slice(1) }}</p>
              </div>
              <div class="review-section">
                <h4>Course Sections</h4>
                <div v-for="(section, index) in formData.sections" :key="index" class="section-review">
                  <h5>Section {{ index + 1 }}: {{ section.title }}</h5>
                  <div v-html="section.description"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="step-navigation">
      <button
        @click="previousStep"
        class="btn-secondary"
        :disabled="currentStep === 0"
      >
        Previous
      </button>
      <div class="right-buttons">
        <button
          v-if="currentStep < steps.length - 1"
          @click="nextStep"
          class="btn-primary"
          :disabled="!canProceed"
        >
          Next
        </button>
        <button
          v-else
          @click="submitCourse"
          class="btn-success"
          :disabled="!isFormValid || isSubmitting"
        >
          {{ isSubmitting ? 'Creating Course...' : 'Quick Create Course' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import Sidebar from './Sidebar.vue'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

export default {
  name: 'CourseBuilder',
  components: {
    Sidebar,
    ckeditor: CKEditor.component
  },
  setup() {
    const editor = ClassicEditor
    const editorConfig = {
      toolbar: [
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'indent',
        'outdent',
        '|',
        'blockQuote',
        'insertTable',
        'undo',
        'redo'
      ]
    }

    const arabicEditorConfig = {
      ...editorConfig,
      language: 'ar',
      contentsLangDirection: 'rtl'
    }

    const steps = [
      { label: 'Basic Info' },
      { label: 'Structure' },
      { label: 'Content' },
      { label: 'Review' }
    ]

    const currentStep = ref(0)
    const categories = ref([])
    const instructors = ref([])
    const isSubmitting = ref(false)
    const errorMessage = ref('')
    const lessons = ref([])

    const formData = ref({
      title_en: '',
      title_ar: '',
      description: '',
      description_ar: '',
      cover_image: null,
      cover_image_ar: null,
      science_behind_it: null,
      category: '',
      instructor: '',
      duration: null,
      level: '',
      pricingType: '',
      price: null,
      sections: [
        {
          title: '',
          description: '',
          video_id: null
        }
      ]
    })

    const handleFileUpload = (event, field) => {
      const file = event.target.files[0]
      if (file) {
        formData.value[field] = file
      }
    }

    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://api.kewaar.com/api/admin/coursecategories2', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        const result = await response.json()
        if (result.success) {
          categories.value = result.data.map(category => ({
            id: category.id,
            name: category.title || category.title_ar,
            title_ar: category.title_ar
          }))
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
        errorMessage.value = 'Failed to fetch categories'
      }
    }

    const fetchInstructors = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://api.kewaar.com/api/admin/instructors1', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        const result = await response.json()
        if (result.success) {
          instructors.value = Array.isArray(result.data) ? result.data : [];
        } else {
          instructors.value = [];
        }
      } catch (error) {
        console.error('Error fetching instructors:', error)
        errorMessage.value = 'Failed to fetch instructors'
        instructors.value = [];
      }
    }

    const fetchVideos = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://api.kewaar.com/api/admin/available-videos', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          lessons.value = data.data.videos.map(video => ({
            id: video.video_id,
            title: video.title,
            description: video.description,
            duration: video.duration,
            mp4Url: video.mp4_url,
            hlsUrl: video.hls_url,
            thumbnailUrl: video.thumbnail_url,
            createdAt: video.created_at
          }));
        } else {
          console.error('Failed to fetch videos:', data.message);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    }

    onMounted(() => {
      fetchCategories()
      fetchInstructors()
      fetchVideos()
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 0:
          return formData.value.title_en && 
                 formData.value.title_ar &&
                 formData.value.description && 
                 formData.value.description_ar &&
                 formData.value.cover_image &&
                 formData.value.cover_image_ar &&
                 formData.value.category && 
                 formData.value.instructor &&
                 formData.value.pricingType && 
                 (formData.value.pricingType !== 'paid' || formData.value.price)
        case 1:
          return formData.value.duration && formData.value.level
        case 2:
          return formData.value.sections.every(section => 
            section.title && 
            section.description && 
            section.video_id !== null && 
            section.video_id !== ''
          )
        default:
          return true
      }
    })

    const isFormValid = computed(() => {
      return (
        formData.value.title_en &&
        formData.value.title_ar &&
        formData.value.description &&
        formData.value.description_ar &&
        formData.value.cover_image &&
        formData.value.cover_image_ar &&
        formData.value.category &&
        formData.value.instructor &&
        formData.value.duration &&
        formData.value.level &&
        formData.value.sections.length > 0 &&
        formData.value.sections.every(section => 
          section.title && 
          section.description && 
          section.video_id !== null && 
          section.video_id !== ''
        )
      )
    })

    const nextStep = () => {
      if (currentStep.value < steps.length - 1 && canProceed.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--
      }
    }

    const addSection = () => {
      formData.value.sections.push({
        title: '',
        description: '',
        video_id: null
      })
    }

    const removeSection = (index) => {
      formData.value.sections.splice(index, 1)
    }

    const submitCourse = async () => {
      if (isFormValid.value && !isSubmitting.value) {
        isSubmitting.value = true
        errorMessage.value = ''
        
        try {
          const token = localStorage.getItem('token')
          const formDataToSend = new FormData()

          // Add basic course information with correct field names
          formDataToSend.append('title', formData.value.title_en)  // Changed from title_en to title
          formDataToSend.append('title_ar', formData.value.title_ar)
          formDataToSend.append('description', formData.value.description)
          formDataToSend.append('description_ar', formData.value.description_ar)
          formDataToSend.append('category_id', formData.value.category)
          formDataToSend.append('instructor_id', Number(formData.value.instructor))  // Ensure it's a number
          formDataToSend.append('duration', formData.value.duration)
          formDataToSend.append('level', formData.value.level)
          formDataToSend.append('pricing_type', formData.value.pricingType)
          formDataToSend.append('status', 'active')  // Add missing status field
          
          // Handle price field
          if (formData.value.pricingType === 'paid') {
            formDataToSend.append('price', formData.value.price)
          } else {
            formDataToSend.append('price', 0)  // Set default price for subscription
          }

          // Add files
          formDataToSend.append('cover_image', formData.value.cover_image)
          formDataToSend.append('cover_image_ar', formData.value.cover_image_ar)
          if (formData.value.science_behind_it) {
            formDataToSend.append('science_behind_it', formData.value.science_behind_it)
          }

          // Add sections
          formDataToSend.append('sections', JSON.stringify(formData.value.sections.map(section => ({
            title: section.title,
            description: section.description,
            video_id: Number(section.video_id)  // Ensure video_id is a number
          }))))

          const response = await fetch('https://api.kewaar.com/api/admin/courses', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          })

          const result = await response.json()
          if (result.success) {
            // Redirect to courses list or show success message
            window.location.href = '/courses'
          } else {
            errorMessage.value = result.message || 'Failed to create course'
            if (result.errors) {
              // Show detailed validation errors
              errorMessage.value = Object.entries(result.errors)
                .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
                .join('\n')
            }
          }
        } catch (error) {
          console.error('Error creating course:', error)
          errorMessage.value = 'Failed to create course. Please try again.'
        } finally {
          isSubmitting.value = false
        }
      }
    }

    const goToStep = (index) => {
      // Only allow going to completed steps or the next available step
      if (index <= currentStep.value || (index === currentStep.value + 1 && canProceed.value)) {
        currentStep.value = index
      }
    }

    const deleteCourse = async (courseId) => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`https://api.kewaar.com/api/admin/courses/${courseId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        const result = await response.json()
        if (result.success) {
          // Handle successful deletion
          window.location.href = '/courses' // Redirect to courses list
        } else {
          errorMessage.value = result.message || 'Failed to delete course'
        }
      } catch (error) {
        console.error('Error deleting course:', error)
        errorMessage.value = 'Failed to delete course. Please try again.'
      }
    }

    return {
      steps,
      currentStep,
      formData,
      categories,
      instructors,
      canProceed,
      isFormValid,
      nextStep,
      previousStep,
      addSection,
      removeSection,
      submitCourse,
      handleFileUpload,
      editor,
      editorConfig,
      arabicEditorConfig,
      isSubmitting,
      errorMessage,
      goToStep,
      lessons,
      deleteCourse
    }
  }
}
</script>

<style scoped>
.course-builder {
  display: flex;
  min-height: 100vh;
}

.course-builder-content {
  flex: 1;
  padding: 2rem;
  margin-left: 280px;
  background: #f8f9fa;
}

.course-builder-header {
  margin-bottom: 2rem;
}

.step-container {
  display: flex;
  gap: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

.step-sidebar {
  width: 250px;
  border-right: 1px solid #e0e0e0;
  padding-right: 1rem;
}

.step-tab {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.step-tab:hover {
  background: #f5f5f5;
}

.step-tab.active {
  background: #e3f2fd;
  color: #1976D2;
}

.step-tab.completed {
  color: #4CAF50;
}

.step-tab-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-weight: 500;
}

.step-tab.active .step-tab-number {
  background: #1976D2;
  border-color: #1976D2;
  color: white;
}

.step-tab.completed .step-tab-number {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.step-tab-label {
  flex: 1;
  font-weight: 500;
}

.step-tab-status {
  width: 24px;
  display: flex;
  justify-content: center;
}

.status-icon {
  font-size: 1.2rem;
}

.step-content {
  flex: 1;
  padding-left: 2rem;
}

.progress-bar {
  display: none; /* Hide the old progress bar */
}

@media (max-width: 1200px) {
  .course-builder-content {
    margin-left: 0;
  }
  
  .step-container {
    flex-direction: column;
  }
  
  .step-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    padding-right: 0;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }
  
  .step-content {
    padding-left: 0;
  }
}

.step {
  margin-top: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1.1rem;
}

.sections-container {
  margin-top: 1rem;
}

.section {
  border: 1px solid #ddd;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.step-navigation {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.right-buttons {
  display: flex;
  gap: 1rem;
}

.btn-primary,
.btn-secondary,
.btn-success {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.review-content {
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  background: #f5f5f5;
  border-radius: 4px;
}

.review-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.review-section:last-child {
  margin-bottom: 0;
}

.error-message {
  color: #f44336;
  margin: 1rem 0;
  padding: 0.5rem;
  background: #ffebee;
  border-radius: 4px;
}

.section-review {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.section-review h5 {
  margin-bottom: 0.5rem;
  color: #1976D2;
}

/* Add styles for CKEditor */
:deep(.ck-editor__editable) {
  min-height: 200px;
}

:deep(.ck-editor__editable_inline) {
  padding: 0 1rem;
}

:deep([dir="rtl"] .ck.ck-editor__editable) {
  text-align: right;
}
</style> 